import { Controller } from "@hotwired/stimulus"
import { badgeClasses } from '@/utils/badge_classes'
export default class extends Controller {
  static targets = [
    "comment",
    "avatar",
    "header",
    "message"
  ]

  connect() {
    this.isNearBottom = true
    this.bannerShown = false

    if (!this.hasCurrentUserIdValue) {
      const metaTag = document.querySelector('meta[name="current-user-id"]')
      if (metaTag) {
        this.currentUserIdValue = metaTag.content
      }
    }

    this.scrollToBottom()
    this.setupObserver()

    this.element.addEventListener('turbo:frame-load', this.handleTurboFrameLoad.bind(this))
    this.element.addEventListener('turbo:render', this.handleTurboRender.bind(this))

    setTimeout(() => {
      this.applyCommentLayout()
      this.groupCommentsByDate()
    }, 100)
  }

  handleTurboFrameLoad(event) {
    this.scrollToBottom()
    this.applyCommentLayout()
    this.groupCommentsByDate()
  }

  handleTurboRender(event) {
    this.applyCommentLayout()
    this.groupCommentsByDate()
  }

  scrollToBottom() {
    if (!this.element.lastElementChild) return

    const parentScrollTop = window.scrollY
    this.element.lastElementChild.scrollIntoView({
      behavior: 'auto',
      block: 'end'
    })

    window.scrollTo({
      top: parentScrollTop,
      behavior: 'auto'
    })
  }

  setupObserver() {
    this.observer = new MutationObserver((mutations) => {
      let newCommentAdded = false
      let newComment = null

      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.hasAttribute('data-chat-target') &&
                  node.getAttribute('data-chat-target') === 'comment') {
                newCommentAdded = true
                newComment = node
                this.processComment(node)
              }
              else {
                const comments = node.querySelectorAll('[data-chat-target="comment"]')
                if (comments.length > 0) {
                  newCommentAdded = true
                  newComment = comments[comments.length - 1] // Last comment added
                  comments.forEach(comment => this.processComment(comment))
                }
              }
            }
          })
        }
      })

      if (newCommentAdded && newComment) {
        this.checkAndInsertBanner()
        this.groupCommentsByDate()
        this.scrollToBottom()
      }
    })

    this.observer.observe(this.element, {
      childList: true,
      subtree: true
    })
  }

  checkAndInsertBanner() {
    const dataElement = this.element.querySelector('[data-last-read-at]')

    if (this.bannerShown) return

    const currentUserId = document.querySelector('meta[name="current-user-id"]').content

    // Find the user's last comment
    let userLastComment = null
    for (let i = this.commentTargets.length - 1; i >= 0; i--) {
      if (this.commentTargets[i].dataset.creatorId === currentUserId) {
        userLastComment = this.commentTargets[i]
        break
      }
    }

    if (!userLastComment) { return }

    const userLastCommentTime = new Date(userLastComment.dataset.createdAt)

    let firstCommentAfterUser = null

    for (let i = 0; i < this.commentTargets.length; i++) {
      const comment = this.commentTargets[i]
      const commentTime = new Date(comment.dataset.createdAt)
      const commentCreator = comment.dataset.creatorId
      const isBot = comment.dataset.botCreator === 'true'

      // If this comment is after user's last comment AND not by the user AND not by a bot
      if (commentTime > userLastCommentTime &&
          commentCreator !== currentUserId &&
          !isBot) {

        // If we have lastReadAt data, check if the comment is after that time
        if (dataElement && dataElement.dataset.lastReadAt) {
          const lastReadAt = new Date(dataElement.dataset.lastReadAt)
          if (commentTime <= lastReadAt) {
            // Comment was already read, skip it
            continue
          }
        }

        firstCommentAfterUser = comment
        break
      }
    }

    // If no qualifying comment found, don't show banner
    if (!firstCommentAfterUser) {
      return
    }

    const bannerHTML = `
      <div class="position-relative my-4" data-banner-type="new-replies">
        <hr class="border-2 border-primary">
        <div class="position-absolute top-50 start-50 translate-middle px-3 bg-white">
          <small class="text-primary">New replies since your last reply</small>
        </div>
      </div>
    `
    firstCommentAfterUser.insertAdjacentHTML('beforebegin', bannerHTML)
    this.bannerShown = true
  }

  applyCommentLayout() {
    this.commentTargets.forEach(comment => {
      if (comment.dataset.layoutProcessed !== 'true') {
        this.processComment(comment)
      }
    })

    this.checkAndInsertBanner()
  }

  processComment(comment) {
    if (comment.dataset.layoutProcessed === 'true') return

    const currentUserId = this.currentUserIdValue || document.querySelector('meta[name="current-user-id"]').content
    const creatorId = comment.dataset.creatorId
    const messageTarget = comment.querySelector('[data-chat-target="message"]')
    const headerTarget = comment.querySelector('[data-chat-target="header"]')

    // Add timestamp to the message
    if (messageTarget && comment.dataset.createdAt) {
      const timestamp = new Date(comment.dataset.createdAt)
      const timeStr = timestamp.toLocaleTimeString("en-US", { hour: 'numeric', minute: '2-digit' })

      // Create timestamp element and add it to the message
      const timestampEl = document.createElement('div')
      timestampEl.style = "line-height: 1;"
      if (creatorId === currentUserId) { timestampEl.classList.add('text-end') }
      else { timestampEl.classList.add('text-start')  }
      timestampEl.innerHTML = `<small class="fs-7">${timeStr}</small>`
      messageTarget.insertAdjacentElement('afterend', timestampEl)
    }

    if (creatorId === currentUserId) {
      comment.classList.add('flex-row-reverse')
      comment.classList.remove('flex-row')
      const avatarTarget = comment.querySelector('[data-chat-target="avatar"]')
      if (avatarTarget) {
        avatarTarget.style.display = 'none'
      }
      if (headerTarget) {
        headerTarget.classList.add('flex-row-reverse')
        headerTarget.classList.remove('flex-row')
      }

      if (messageTarget) {
        messageTarget.classList.add("bg-primary", "text-dark", "ms-3", "bg-opacity-25")
        messageTarget.classList.remove("bg-light", "border", "me-3")
      }
      const links = messageTarget?.querySelectorAll('a') || []
      links.forEach(link => {
        link.classList.add('link-underline-light', 'text-light')
      })
    }

    if (messageTarget && messageTarget.dataset.internal === "true") {
      messageTarget.classList.add("bg-warning", "text-dark", "bg-opacity-50", "mt-1")
      messageTarget.classList.remove("text-white", "bg-light", "bg-primary")
    }

    if (headerTarget && headerTarget.dataset.internal === "true") {
      headerTarget.insertAdjacentHTML(
        'afterbegin',
        `<span class='${badgeClasses({ variant: 'warning', subtle: true, extra: 'align-self-center' })}'>Internal</span>`
      )
    }

    const links = comment.querySelectorAll('a')
    links.forEach(link => {
      if (!link.hasAttribute("data-turbo-frame")) {
        link.setAttribute("target", "_blank");
      }
    })

    comment.dataset.layoutProcessed = 'true'
  }

  groupCommentsByDate() {
    // Remove existing date headers down below in case we need to
    this.element.querySelectorAll('[data-date-header]').forEach(header => header.remove())

    // Group comments by date
    const commentsByDate = {}

    this.commentTargets.forEach(comment => {
      if (!comment.dataset.createdAt) return

      const date = new Date(comment.dataset.createdAt)
      const dateStr = date.toLocaleDateString([], {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })

      if (!commentsByDate[dateStr]) {
        commentsByDate[dateStr] = []
      }

      commentsByDate[dateStr].push(comment)
    })

    // Sort dates (oldest first)
    const sortedDates = Object.keys(commentsByDate).sort((a, b) => {
      return new Date(a) - new Date(b)
    })

    // Insert date headers before the first comment of each date
    sortedDates.forEach(dateStr => {
      const commentsForDate = commentsByDate[dateStr]
      if (commentsForDate.length === 0) return

      const firstComment = commentsForDate[0]

      // Create date header
      const dateHeader = document.createElement('div')
      dateHeader.classList.add('text-center', 'my-3')
      dateHeader.dataset.dateHeader = dateStr

      dateHeader.innerHTML = `
        <div class="position-relative">
          <hr class="border-1">
          <div class="position-absolute top-50 start-50 translate-middle px-3 bg-white">
            <small class="text-muted">${dateStr}</small>
          </div>
        </div>
      `

      // Insert before the first comment of the day
      firstComment.parentNode.insertBefore(dateHeader, firstComment)
    })
  }
}
